import React, { Component } from 'react';
import { Loader, Container } from 'semantic-ui-react';
import { toast } from 'react-toastify';
import sortBy from 'lodash/sortBy';
import map from 'lodash/map';

import PackageList from './PackageList';
import ManagePackage from './ManagePackage';
import PackageLaunch from './PackageLaunch';

import { getPages } from '../../fbApi/Page';
import { getAudiences } from '../../fbApi/Audience';
import { getProductCatalogs } from '../../fbApi/ProductCatalog';
import { getAccounts } from '../../services/AccountService';
import { makeGetWithParam } from '../../services/ServiceManager';
import {
  getPackages,
  getPackagesWithName,
  getPackage,
  deletePackage
} from '../../services/PackageServiceManager';
import { getAdPrototypes } from '../../services/AdPrototypeService';
import { getTags } from '../../services/TagService';
import { getUtmProfiles } from '../../services/UtmProfilesService';
import { updatePackageBuilderLaunchUrl } from '../../utils/urls';

import './PackageBuilderV2.css';

class PackageBuilderV2 extends Component {
  state = {
    section: 'search',
    packages: null,
    tags: [],
    pages: [],
    accountOptions: [],
    audienceOptions: [],
    productCatalogs: [],
    adPrototypesCards: [],
    utmProfiles: [],
    adPrototypes: null,
    loading: false,
    pagination: {
      activePage: 1,
      totalPages: 2
    }
  };

  componentDidMount() {
    this.props.setSectionTitle('Package Builder V2');
    this.props.saveGTMEvent('Package Builder V2', 'Alexia');

    this.fetchData();
  }

  containsId = () => {
    const { href } = window.location;
    let packageId;
    if (href.includes('#id=')) {
      packageId = Number(href.split('#id=')[1]);
    } else {
      packageId = -1;
    }
    if (packageId >= 0) {
      this.launchPackage(packageId, href);
      return true;
    }
    return false;
  };

  fetchData = () => {
    this.setState(
      {
        ...this.state,
        loading: true
      },
      () => {
        const { pagination } = this.state;
        const { activePage } = pagination;

        // re-writing the wrapper functions for each async functions because returned value from Promise.all is a list.
        // so there should be a way to distincguish which one is which for post processing.
        Promise.all([
          this.fetchPackages(activePage), // packages... This returns pagination information along with the packages
          this.fetchAdPrototypes(), // AdPrototypes
          this.fetchPages(), // pages
          this.fetchAccounts(), // accounts
          this.fetchTags(), // tags
          this.fetchProductCatalogs(), // productCatalogs
          this.fetchUTMProfiles(), // utmProfiles
          this.fetchAudiences()
        ]).then((result) => {
          if (!result || result.length === 0)
            throw new Error('There was error while fetching data.');

          const fetchedDataObject = {};

          for (const data of result) {
            const keys = Object.keys(data);

            if (!keys || keys.length > 1)
              throw new Error('Only one key must be in this data object.');

            const key = keys[0];
            const { [key]: resultObject } = data;

            if (key === 'AdPrototypes') {
              const { adPrototypes, adPrototypesCards } = resultObject;
              fetchedDataObject.adPrototypes = adPrototypes;
              fetchedDataObject.adPrototypesCards = adPrototypesCards;
            } else {
              fetchedDataObject[key] = resultObject;
            }
          }

          // this.fetchPackages() returns pagination information along with the packages.
          const { packages } = fetchedDataObject;
          const { pagination: returnedPagination } = packages || {};

          this.setState(
            {
              ...this.state,
              ...fetchedDataObject,
              pagination: returnedPagination
                ? { ...returnedPagination }
                : { ...this.state.pagination }
            },
            () => {
              this.setState(
                {
                  ...this.state,
                  loading: false
                },
                () => {
                  this.containsId();
                }
              );
            }
          );
        });
      }
    );
  };

  fetchAudiences = async () => {
    const accountId = '723569031396032'; // account ID with all audiences
    let audienceOptions = [];
    try {
      const token = this.props.getFbToken ? this.props.getFbToken() : '';
      const audiencesResult = await getAudiences(accountId, token);
      const audiences = audiencesResult[0].data.concat(audiencesResult[1].data);
      audienceOptions = sortBy(
        map(audiences, (op, index) => ({
          key: index,
          value: op.id,
          text: op.name
        })),
        'text'
      );
      return { audienceOptions };
    } catch (error) {
      toast.warn('Audiences could not be retrieved');
      return { audienceOptions };
    }
  };

  fetchUTMProfiles = async () => {
    const response = await getUtmProfiles();
    return {
      utmProfiles: response.utmProfiles
    };
  };

  fetchPackages = async (activePage) => {
    const { groups, email } = this.props.user;
    const applyGuestFilter = ['NU'].includes(groups[0]);

    const response = await getPackages(
      activePage,
      applyGuestFilter ? email : null
    );
    let { packages: packageList } = response || {};

    if (!packageList) {
      packageList = { packages: [], ads: [], pagination: {} };
    }

    return {
      packages: packageList
    };
  };

  fetchPackagesWithName = async (activePage, name = null) => {
    const { groups, email } = this.props.user;
    const applyGuestFilter = ['NU'].includes(groups[0]);

    const response = await getPackagesWithName(
      activePage,
      name,
      applyGuestFilter ? email : null
    );
    let { packages: packageList } = response || {};

    if (!packageList) {
      packageList = { packages: [], ads: [], pagination: {} };
    }
    this.setState({
      ...this.state,
      packages: packageList
    });

    return {
      packages: packageList
    };
  };

  fetchAdPrototypes = async () => {
    try {
      let getAdPrototypesRes;
      const { groups, email } = this.props.user;

      // This leaves room for possibility for future guests
      const applyGuestFilter = ['NU'].includes(groups[0]);

      if (applyGuestFilter) {
        const query = `?created_by=${encodeURIComponent(email)}`;
        getAdPrototypesRes = await makeGetWithParam(
          'service/adprototype/adprototypes/filter',
          null,
          query
        );
      } else {
        getAdPrototypesRes = await getAdPrototypes();
      }

      const { result } = getAdPrototypesRes;
      const { adPrototypes: resultPrototypes, cards } = result;

      return {
        AdPrototypes: {
          adPrototypes: resultPrototypes,
          adPrototypesCards: cards
        }
      };
    } catch (error) {
      toast.error(
        'An error occurred while loading relevant Ad Prototypes. Reload the page and try again.',
        {
          position: 'bottom-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        }
      );
      console.log('Something went wrong in fetchAdPrototypes(): ', error);
      return {
        AdPrototypes: {
          adPrototypes: [],
          adPrototypesCards: []
        }
      };
    }
  };

  fetchPages = async () => {
    const { getFbToken, user } = this.props;
    const pages = await getPages(getFbToken(), user.groups[0]);

    if (!pages || pages.length < 2) {
      return {
        pages: []
      };
    }

    const filteredPages = pages[0].data
      .concat(pages[1].data)
      .filter((page) => page.access_token);

    const pagesOptions = sortBy(
      map(filteredPages, (page, index) => ({
        key: index,
        value: { pageId: page.id, access_token: page.access_token },
        text: page.name
      })),
      'text'
    );

    return {
      pages: pagesOptions
    };
  };

  fetchAccounts = async () => {
    const accounts = await getAccounts(this.props.user.groups[0]);

    const accountOptions = sortBy(
      map(accounts, (op, index) => ({
        key: index,
        value: op.account_id,
        text: op.name
      })),
      'text'
    );

    return {
      accountOptions
    };
  };

  fetchTags = async () => {
    const { groups, email } = this.props.user;
    const applyGuestFilter = ['NU'].includes(groups[0]);

    const result = await getTags(applyGuestFilter ? email : null);
    const tags = sortBy(
      result.tags.filter((tag) => tag.category === 'oem'),
      'value'
    );
    const options = tags.map((tag, index) => ({
      key: index,
      value: tag.id,
      text: tag.value,
      category: tag.category
    }));

    return {
      tags: options
    };
  };

  fetchProductCatalogs = async () => {
    const { getFbToken, user } = this.props;

    const result = await getProductCatalogs(
      getFbToken(),
      undefined,
      user.groups[0] // users only have one group in this app
    );

    const catalogs = result ? result.data : [];
    const catalogSelectionList = catalogs.map((catalog, index) => ({
      key: index,
      text: catalog.name,
      value: catalog.id
    }));

    return {
      productCatalogs: catalogSelectionList
    };
  };

  reloadPackages = async (activePage) => {
    this.setState({
      ...this.state,
      section: 'search',
      loading: true
    });

    const response = await this.fetchPackages(activePage);
    const { packages } = response;
    const { pagination } = packages;

    this.setState((prevState) => ({
      ...prevState,
      packages,
      loading: false,
      pagination
    }));
  };

  onPageChange = async (activePage) => {
    await this.reloadPackages(activePage);
  };

  newPackage = () => {
    this.setState({ section: 'newPackage', packageToEdit: '' });
  };

  searchPage = () => {
    this.setState({ section: 'search' }, async () => {
      // await this.reloadPackages();
      this.fetchData();
      updatePackageBuilderLaunchUrl();
    });
  };

  launchPackage = async (id, href = '') => {
    try {
      const result = await getPackage(id);
      let packageToLaunch = null;

      if (result.statusCode === 200) {
        packageToLaunch = result.packageData;
      }

      this.setState(
        {
          section: 'launchPackage',
          packageToLaunch: this.getAdPrototypesForPackage(packageToLaunch)
        },
        () => {
          if (!href.includes('#id=')) {
            updatePackageBuilderLaunchUrl(id);
          }
        }
      );
    } catch (error) {
      toast.error(`Package Missing or Deleted and could not be found`, {
        position: 'bottom-center',
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      console.log('Something went wrong in launchPackage(): ', error);
    }
  };

  editPackage = async (id) => {
    try {
      const result = await getPackage(id);
      let packageToEdit = null;

      if (result.statusCode === 200) {
        packageToEdit = result.packageData;
      }
      this.setState({ section: 'newPackage', packageToEdit });
    } catch (error) {
      toast.error(
        `An error occured with getting packages and/or ads in the package to launch! Try remaking the package!`,
        {
          position: 'bottom-center',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        }
      );
      console.log('Something went wrong in launchPackage(): ', error);
    }
  };

  deletePackage = async (id) => {
    await deletePackage(id);
    await this.reloadPackages();
  };

  getAdPrototypesForPackage = (packageToLaunch) => {
    const { adPrototypes, adPrototypesCards } = this.state;
    const { ads: packageAds } = packageToLaunch;

    const newAds =
      packageAds instanceof Array
        ? packageAds.map((ad) => {
            const adPrototype =
              adPrototypes instanceof Array
                ? adPrototypes.find(
                    (item) => item.id === parseInt(ad.ad_prototype_id, 10)
                  )
                : [];
            const cards =
              adPrototypesCards instanceof Array
                ? sortBy(
                    adPrototypesCards.filter(
                      (card) => card.ad_prototype_id === adPrototype.id
                    ),
                    'id'
                  )
                : [];

            const newPrototype = {
              ...adPrototype,
              cards
            };

            let newAdName = ad.name;

            if (
              (ad.type === 'Cinemagraph' ||
                adPrototype.type === 'Cinemagraph') &&
              ad.name.includes('_Video_')
            ) {
              newAdName = ad.name.replace('_Video_', '_Cinemagraph_');
            } else if (
              (ad.type === 'Video' || adPrototype.type === 'Video') &&
              ad.name.includes('_Cinemagraph_')
            ) {
              newAdName = ad.name.replace('_Cinemagraph_', '_Video_');
            }

            return {
              ...ad,
              name: newAdName,
              ad: newPrototype
            };
          })
        : [];
    return {
      ...packageToLaunch,
      ads: newAds
    };
  };

  render() {
    const {
      section,
      loading,
      packages,
      tags,
      packageToEdit,
      packageToLaunch,
      pages,
      accountOptions,
      adPrototypes,
      productCatalogs,
      pagination,
      utmProfiles,
      audienceOptions
    } = this.state;
    return (
      <Container fluid>
        {loading && (
          <div className="package-builder-loading">
            <span>
              {' '}
              <Loader inline className="active" /> Loading...{' '}
            </span>
          </div>
        )}

        {!loading && section === 'search' && (
          <PackageList
            packages={packages}
            pagination={pagination}
            newPackage={this.newPackage}
            launchPackage={this.launchPackage}
            deletePackage={this.deletePackage}
            editPackage={this.editPackage}
            onPageChange={this.onPageChange}
            adPrototypes={adPrototypes}
            reloadPackages={this.reloadPackages}
            fetchPackagesWithName={this.fetchPackagesWithName}
          />
        )}

        {section === 'newPackage' && (
          <ManagePackage
            searchPage={this.searchPage}
            tags={tags}
            adPrototypes={adPrototypes}
            packageToEdit={packageToEdit}
            getFbToken={this.props.getFbToken}
            audienceOptions={audienceOptions}
          />
        )}
        {section === 'launchPackage' && (
          <PackageLaunch
            packageToLaunch={packageToLaunch}
            getFbToken={this.props.getFbToken}
            pages={pages}
            accounts={accountOptions}
            adPrototypes={adPrototypes}
            productCatalogs={productCatalogs}
            searchPage={this.searchPage}
            utmProfiles={utmProfiles}
            audienceOptions={audienceOptions}
            fetchPages={this.fetchPages}
          />
        )}
      </Container>
    );
  }
}

export default PackageBuilderV2;



// WEBPACK FOOTER //
// ./src/screens/PackageBuilder/PackageBuilderV2.js